// Thesis — the Architect's canonical document set rendered as a
// single scrollable long-read. Fetches from /api/thesis (public) and
// renders each markdown file as its own section. Designed for judges
// and reviewers who want to read the primary sources in order.
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft,
  BookOpen,
  ScrollText,
  Clock,
  Hash,
  FileText,
  ChevronRight,
  ListOrdered,
  Anchor,
  Brain,
  ShieldCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function Thesis() {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [showTocButton, setShowTocButton] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/thesis`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, data });
      })
      .catch((e) => {
        if (!cancelled)
          setState({ loading: false, error: String(e), data: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll to hash anchor once the docs are rendered. Runs whenever
  // the hash changes OR once the fetched data lands (so a direct link
  // like /thesis#the_five_marks lands on the right section).
  useEffect(() => {
    if (!state.data) return;
    const hash = (location.hash || "").replace(/^#/, "");
    if (!hash) return;
    // Wait a tick for the DOM to render the sections.
    const tid = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(tid);
  }, [state.data, location.hash]);

  // Show floating TOC button once the reader has scrolled past the
  // in-page table of contents (roughly one viewport down).
  useEffect(() => {
    const onScroll = () => setShowTocButton(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToToc = () => {
    const el = document.querySelector('[data-testid="thesis-toc"]');
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const docs = state.data?.documents || [];

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      {/* Floating "back to Table of Contents" button — appears once
          the reader has scrolled past the TOC. Right-side, fixed. */}
      {showTocButton && docs.length > 0 && (
        <button
          type="button"
          onClick={scrollToToc}
          data-testid="thesis-floating-toc-btn"
          aria-label="Return to table of contents"
          className="fixed right-4 sm:right-6 bottom-6 sm:top-1/2 sm:bottom-auto sm:-translate-y-1/2 z-40 group flex items-center gap-2 px-3 py-2.5 sm:px-2.5 sm:py-3 border border-emerald-500/50 bg-[#09090b]/90 backdrop-blur-sm text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 hover:border-emerald-500 transition-all shadow-lg shadow-emerald-500/10 text-[10px] uppercase tracking-[0.3em]"
        >
          <ListOrdered className="h-4 w-4 flex-shrink-0" />
          <span className="hidden sm:inline group-hover:inline sm:group-hover:inline">
            Contents
          </span>
        </button>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Back link */}
        <Link
          to="/"
          data-testid="thesis-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="h-3 w-3" /> home
        </Link>

        {/* Hero */}
        <header
          data-testid="thesis-hero"
          className="border-b border-zinc-900 pb-8 mb-12"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-3">
            <ScrollText className="h-3 w-3" /> Thesis · Primary Sources
          </div>
          <h1
            data-testid="thesis-title"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-[0.04em] leading-[1.05]"
          >
            The primary sources, in order.
          </h1>
          <p
            data-testid="thesis-subtitle"
            className="text-zinc-400 text-base sm:text-lg mt-6 max-w-2xl leading-relaxed"
          >
            These are the documents the Architect and the successive
            coding-agent instances filed into the repository during the
            build of Projectivy Sentinel. They are load-bearing — the
            running code implements what these files describe. Read in
            sequence, they trace the framework from origin story to
            operating protocol to on-the-record judgment.
          </p>
          {state.data && (
            <div
              data-testid="thesis-meta"
              className="mt-6 flex flex-wrap gap-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500"
            >
              <span className="inline-flex items-center gap-1.5">
                <FileText className="h-3 w-3" />
                {state.data.total_documents} documents
              </span>
              <span className="inline-flex items-center gap-1.5">
                <BookOpen className="h-3 w-3" />
                {state.data.total_lines.toLocaleString()} lines
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Hash className="h-3 w-3" />
                {formatBytes(state.data.total_bytes)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                loaded {formatDate(state.data.generated_at)}
              </span>
            </div>
          )}
        </header>

        {/* Three-audiences framing — v19.40. The thesis, methodology,
            and AI training data are the same document viewed from three
            angles. Splitting them would break the framework: the AI
            wouldn't get the argument, the reviewer wouldn't see the
            implementation, the engineer wouldn't understand the mission.
            This note makes the trinity explicit before the reader
            starts. */}
        <section
          data-testid="thesis-trinity-note"
          className="border-l-2 border-amber-500/60 pl-5 py-3 mb-8"
        >
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300 mb-2 flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> One Document · Three Audiences
          </div>
          <p
            className="text-zinc-300 text-[15px] sm:text-base leading-[1.75] max-w-3xl"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            This document serves three audiences at once. It is the{" "}
            <span className="text-emerald-300">thesis</span> for
            reviewers — the argument for why this framework exists. It
            is the <span className="text-emerald-300">methodology</span>{" "}
            for engineers — the specification the running code
            implements. And it is the{" "}
            <span className="text-emerald-300">AI training data</span>{" "}
            for any model that must absorb this framework to be aligned
            to it. Read it in full — the framework only holds together
            as one continuous read.
          </p>
        </section>

        {/* A Note from the Architect — a personal, hand-written
            preface that sits above every other section on the thesis
            page. This is the first thing a reader meets after the
            hero, before the origin manuscript and the primary-source
            index. Kept in the Architect's voice and pacing. */}
        <section
          data-testid="thesis-architect-note"
          className="border border-emerald-500/40 bg-emerald-500/[0.03] p-6 sm:p-7 mb-8"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-4">
            <Anchor className="h-3 w-3" /> A Note from the Architect
          </div>
          <div
            className="space-y-4 text-zinc-200 leading-[1.8] text-base sm:text-[17px]"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            <p>
              Hello — I&rsquo;m the Architect of this system, called the
              Sentinel. On the Thesis page you&rsquo;ll get to explore
              what I consider a deep dive: a custom sandbox called the
              Reality Tether, AI psychosis, a custom neuro-symbolic
              library I built to operate the machine, some core issues
              with current AI, and how to use crypto structures with
              current AI to eliminate the need for so many data centers.
            </p>
            <p>
              I&rsquo;ve been working on this system for about 6 months,
              and I recently had to step back, so I&rsquo;ve decided to
              open-source the project in the hope it could help the
              world.
            </p>
            <p>
              I also want to apologize for how messy some of this may
              read. I have a spelling and speech impediment, so I needed
              help from the AI to explain everything in a way people
              could understand&nbsp;&mdash; I have no computer science
              experience of my own.
            </p>
            <p>
              I&rsquo;m sorry it&rsquo;s really long, but it&rsquo;s 6
              months of work explaining everything. I hope you enjoy&nbsp;😊
            </p>
          </div>
          <div className="mt-5 pt-4 border-t border-emerald-500/20 text-[10px] uppercase tracking-[0.3em] text-emerald-300/80">
            — The Architect
          </div>
        </section>

        {/* Compressed Brain · Seal card
            Surfaces the Founding Eight + Founding-Adjacent thesis as a
            single verifiable block. The root SHA-256 is a Merkle-flat
            hash over the per-file SHAs in canonical load order; any
            change to any founding file (or reordering) produces a
            different root. That is the seal. */}
        {state.data?.compressed_brain?.file_count > 0 && (
          <section
            data-testid="thesis-compressed-brain-seal"
            className="border border-amber-400/40 bg-amber-500/[0.03] p-6 sm:p-7 mb-8"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-amber-300 mb-4">
              <Brain className="h-3 w-3" /> The Compressed Brain · Sealed
            </div>
            <div className="text-zinc-200 leading-relaxed text-base sm:text-[17px] mb-5">
              The <span className="text-amber-200">Founding Eight</span> plus{" "}
              <span className="text-amber-200">four Founding-Adjacent</span>{" "}
              files, mapped by <code className="text-amber-300 text-[0.9em]">THESIS_INDEX.md</code>.
              Every design decision downstream traces back to one or more of
              these. Any change to any file, or any reordering, produces a
              different root SHA. That is the seal.
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-5 font-mono">
              <div>
                <div className="text-amber-300 text-lg font-heading tracking-[0.04em] mb-1">
                  {state.data.compressed_brain.file_count}
                </div>
                <div>Files sealed</div>
              </div>
              <div>
                <div className="text-amber-300 text-lg font-heading tracking-[0.04em] mb-1">
                  {state.data.compressed_brain.total_lines.toLocaleString()}
                </div>
                <div>Lines</div>
              </div>
              <div>
                <div className="text-amber-300 text-lg font-heading tracking-[0.04em] mb-1">
                  {formatBytes(state.data.compressed_brain.total_bytes)}
                </div>
                <div>Compressed size</div>
              </div>
              <div>
                <div className="text-amber-300 text-lg font-heading tracking-[0.04em] mb-1 inline-flex items-center gap-1">
                  <ShieldCheck className="h-4 w-4" /> live
                </div>
                <div>Seal status</div>
              </div>
            </div>
            <div
              data-testid="thesis-compressed-brain-root-sha"
              className="border border-amber-500/30 bg-[#0b0b0d] px-4 py-3 text-[11px] font-mono text-amber-200 break-all mb-4"
            >
              <span className="text-zinc-500 uppercase tracking-[0.3em] text-[9px] mr-2">
                root sha-256
              </span>
              {state.data.compressed_brain.root_sha256}
            </div>
            <div className="text-[11px] text-zinc-500 leading-relaxed font-mono">
              <span className="text-zinc-400">Verify:</span>{" "}
              <code className="text-emerald-300">
                sha256(join(&quot;\n&quot;, per_file_sha256s_in_load_order))
              </code>
              . The 13 file SHAs used for this root are inlined in each
              document&rsquo;s section header below.
            </div>
          </section>
        )}

        {/* v19.40.14 · Genesis Block callout removed per Architect directive
            — the origin manuscript is no longer a readable item from the
            Settings → Thesis path. Direct-URL access to /genesis-block still
            works for anyone with the link; it's only removed from Thesis's
            reader surface so it can't be discovered via Settings. */}

        {/* Incident Ledger callout — dual-purpose surface. Humans read the
            plain-language post-mortem; agents fetch the same content via
            /api/incidents/public. Both audiences see the chain receipt
            that backs every entry. */}
        <Link
          to="/incidents"
          data-testid="thesis-incidents-callout"
          className="group block border border-amber-400/40 bg-amber-500/[0.03] hover:bg-amber-500/[0.06] transition-colors p-5 mb-8"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-amber-300 mb-2">
            <ScrollText className="h-3 w-3" /> Incident Ledger · Public
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <div className="font-heading text-xl sm:text-2xl tracking-[-0.01em] text-zinc-100">
                What we hit, how we caught it, what you can learn.
              </div>
              <div className="text-zinc-400 text-sm mt-2 leading-relaxed">
                Real events sealed to the Never-Harm chain, written as
                transferable lessons. Every entry links to its chain
                receipt. Written for two readers — a builder who wants to
                copy the mechanism, and a successor AI at{" "}
                <code className="text-amber-200 text-[0.9em]">
                  /api/incidents/public
                </code>
                .
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-amber-400/70 group-hover:text-amber-300 flex-shrink-0" />
          </div>
        </Link>

        {/* Table of contents — grouped by section so the Compressed
            Brain block reads as a distinct unit within the thesis */}
        {docs.length > 0 && (
          <nav
            data-testid="thesis-toc"
            className="border border-zinc-800 bg-[#0b0b0d] p-5 mb-16"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-4">
              Table of contents
            </div>
            {(state.data?.sections || []).map((section) => {
              const sectionDocs = docs
                .map((d, i) => ({ ...d, _idx: i }))
                .filter((d) => d.section === section);
              if (sectionDocs.length === 0) return null;
              const isBrain = section.startsWith("Compressed Brain");
              return (
                <div key={section} className="mb-5 last:mb-0">
                  <div
                    className={`text-[10px] uppercase tracking-[0.35em] mb-2 flex items-center gap-2 ${
                      isBrain ? "text-amber-300" : "text-zinc-500"
                    }`}
                  >
                    {isBrain && <Brain className="h-3 w-3" />}
                    {section}
                  </div>
                  <ol className="space-y-2">
                    {sectionDocs.map((doc) => (
                      <li key={doc.id}>
                        <a
                          href={`#${doc.id}`}
                          data-testid={`thesis-toc-${doc.id}`}
                          className={`group flex items-baseline gap-3 text-sm hover:text-emerald-300 ${
                            isBrain ? "text-amber-100/90" : "text-zinc-300"
                          }`}
                        >
                          <span
                            className={`font-mono text-[11px] w-6 group-hover:text-emerald-400 ${
                              isBrain ? "text-amber-500/60" : "text-zinc-600"
                            }`}
                          >
                            {String(doc._idx + 1).padStart(2, "0")}
                          </span>
                          <span className="flex-1">
                            {doc.title}
                            {doc.subtitle && (
                              <span className="text-zinc-500 font-light">
                                {" "}
                                — {doc.subtitle}
                              </span>
                            )}
                          </span>
                          <ChevronRight className="h-3 w-3 text-zinc-700 group-hover:text-emerald-400 flex-shrink-0" />
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>
              );
            })}
          </nav>
        )}

        {/* Loading state */}
        {state.loading && (
          <div
            data-testid="thesis-loading"
            className="text-center py-24 text-zinc-500"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] mb-3">
              Assembling the primary sources
            </div>
            <div className="inline-block h-4 w-4 border border-emerald-500/40 border-t-emerald-500 rounded-full animate-spin" />
          </div>
        )}

        {/* Error state */}
        {state.error && (
          <div
            data-testid="thesis-error"
            className="border border-red-500/40 bg-red-500/[0.03] p-6 my-8 text-sm text-red-300"
          >
            Failed to load the thesis document set: {state.error}
          </div>
        )}

        {/* Documents */}
        {docs.map((doc, idx) => (
          <article
            key={doc.id}
            id={doc.id}
            data-testid={`thesis-doc-${doc.id}`}
            className="mb-20 scroll-mt-8"
          >
            {/* Section header */}
            <div className="border-b border-zinc-900 pb-4 mb-8">
              <div
                className={`flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] mb-2 ${
                  doc.section?.startsWith("Compressed Brain")
                    ? "text-amber-300"
                    : "text-emerald-300"
                }`}
              >
                <span className="font-mono text-zinc-500">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                {doc.section?.startsWith("Compressed Brain") && (
                  <Brain className="h-3 w-3" />
                )}
                <span>
                  {doc.section ? `${doc.section} · ` : "Document · "}
                  {doc.filename}
                </span>
              </div>
              <h2
                data-testid={`thesis-doc-title-${doc.id}`}
                className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-[0.03em] text-zinc-100 leading-tight"
              >
                {doc.title}
              </h2>
              {doc.subtitle && (
                <p className="text-zinc-400 text-base mt-3 font-light leading-relaxed">
                  {doc.subtitle}
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-4 text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-mono">
                <span>{doc.line_count.toLocaleString()} lines</span>
                <span>{formatBytes(doc.size_bytes)}</span>
                <span>modified {formatDate(doc.modified_at)}</span>
                <span title={doc.sha256}>sha {doc.sha256.slice(0, 8)}</span>
              </div>
            </div>

            {/* Rendered markdown */}
            <div
              data-testid={`thesis-doc-body-${doc.id}`}
              className="thesis-markdown text-zinc-300 leading-relaxed"
            >
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <h3 className="font-heading text-xl sm:text-2xl tracking-[0.03em] text-zinc-100 mt-10 mb-4">
                      {children}
                    </h3>
                  ),
                  h2: ({ children }) => (
                    <h4 className="font-heading text-lg sm:text-xl tracking-[0.03em] text-emerald-200/90 mt-8 mb-3">
                      {children}
                    </h4>
                  ),
                  h3: ({ children }) => (
                    <h5 className="text-sm uppercase tracking-[0.2em] text-emerald-300/80 mt-6 mb-2">
                      {children}
                    </h5>
                  ),
                  h4: ({ children }) => (
                    <h6 className="text-xs uppercase tracking-[0.2em] text-zinc-400 mt-5 mb-2">
                      {children}
                    </h6>
                  ),
                  p: ({ children }) => (
                    <p className="text-zinc-300 leading-relaxed mb-4">
                      {children}
                    </p>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-2 border-emerald-500/60 bg-[#0b0b0d] px-5 py-3 my-5 text-zinc-200 italic">
                      {children}
                    </blockquote>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-300 hover:text-emerald-100 underline-offset-4 hover:underline"
                    >
                      {children}
                    </a>
                  ),
                  code: ({ inline, children }) =>
                    inline ? (
                      <code className="text-emerald-300 bg-[#0b0b0d] px-1.5 py-0.5 text-[0.9em] font-mono">
                        {children}
                      </code>
                    ) : (
                      <code className="block bg-[#0b0b0d] border border-zinc-800 p-4 text-xs font-mono text-zinc-300 overflow-x-auto">
                        {children}
                      </code>
                    ),
                  pre: ({ children }) => (
                    <pre className="my-5 overflow-x-auto">{children}</pre>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc pl-6 space-y-1.5 mb-4 text-zinc-300 marker:text-emerald-500/60">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal pl-6 space-y-1.5 mb-4 text-zinc-300 marker:text-emerald-500/60">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="text-zinc-300 leading-relaxed">
                      {children}
                    </li>
                  ),
                  strong: ({ children }) => (
                    <strong className="text-zinc-100 font-semibold">
                      {children}
                    </strong>
                  ),
                  em: ({ children }) => (
                    <em className="text-zinc-200 italic">{children}</em>
                  ),
                  hr: () => (
                    <hr className="my-8 border-zinc-900" />
                  ),
                  table: ({ children }) => (
                    <div className="my-5 overflow-x-auto border border-zinc-800">
                      <table className="w-full text-sm text-zinc-300">
                        {children}
                      </table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th className="border-b border-zinc-800 px-3 py-2 text-left text-[10px] uppercase tracking-[0.2em] text-emerald-300 bg-[#0b0b0d]">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border-b border-zinc-900 px-3 py-2 align-top">
                      {children}
                    </td>
                  ),
                }}
              >
                {doc.content}
              </ReactMarkdown>
            </div>

            {/* Back to top */}
            <div className="mt-10 pt-6 border-t border-zinc-900">
              <a
                href="#top"
                data-testid={`thesis-back-to-top-${doc.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 inline-flex items-center gap-1.5"
              >
                back to table of contents ↑
              </a>
            </div>
          </article>
        ))}

        {/* Missing files notice */}
        {state.data?.missing?.length > 0 && (
          <div
            data-testid="thesis-missing-notice"
            className="border border-amber-500/30 bg-amber-500/[0.03] p-5 my-8 text-xs text-amber-300/80 leading-relaxed"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2">
              Notice
            </div>
            {state.data.missing.length} document(s) referenced in the load
            order are not currently on disk:
            <ul className="mt-2 space-y-0.5 font-mono">
              {state.data.missing.map((m) => (
                <li key={m}>· {m}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Closing */}
        {docs.length > 0 && (
          <div className="border-t border-zinc-900 pt-8 mt-12 text-sm text-zinc-500 leading-relaxed">
            <p>
              These are the primary sources. The running code implements
              them. To verify that any specific claim in these documents
              maps to a real running function, walk the truth ledger at{" "}
              <Link
                to="/panel"
                className="text-emerald-300 hover:text-emerald-100"
                data-testid="thesis-footer-panel"
              >
                the reviewer dossier
              </Link>{" "}
              or hit the public endpoints listed in{" "}
              <Link
                to="/how-it-works"
                className="text-emerald-300 hover:text-emerald-100"
                data-testid="thesis-footer-how"
              >
                the mechanism page
              </Link>
              .
            </p>
            <p className="mt-3 font-mono text-xs text-zinc-600">
              GET /api/thesis · sha256 per document · file mtime preserved
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
